/**
 * Swamithra — the human-readable identifiers.
 *
 * A query is `#12`; a vetting request is `#V12`. US-00-002.30 requires the SAME
 * rendering in four places — My Queries, the query detail, the WhatsApp
 * notification body and the Expert panel — and three of those are in different
 * applications. One formatter is the only way that holds.
 *
 * THE SERVICE SENDS THE RENDERED STRING, NOT THE NUMBER. Every payload carries
 * `reference: "#12"` / `"#V12"` — already formatted, by the one formatter on
 * that side — and no `sequence` field at all. A schema that required a numeric
 * `sequence` parsed nothing: My Vetting Requests rendered zero cards and showed
 * an "unexpected response" state instead of the list.
 *
 * So `reference` is what is read off the wire, and the number is DERIVED from it
 * by `withReference` below for the places that need one — a route parameter, a
 * sort, a test. It is a per-entity counter, not the record key: the opaque `id`
 * is that, and the reference exists so a person can quote "query twelve" on a
 * phone call.
 */

/** `12` → `#12` (US-00-002.1). */
export function formatQueryId(sequence: number): string {
	return `#${sequence}`
}

/** `12` → `#V12` (US-00-004.1). */
export function formatVettingId(sequence: number): string {
	return `#V${sequence}`
}

export type ParsedHumanId = {
	kind: "query" | "vetting"
	sequence: number
}

/**
 * Reads a human-readable id back, for the Expert dashboard search.
 *
 * US-01-001.3 requires an exact query-id search to work "with or without the
 * leading #", so all of `#12`, `12`, `#V12`, `v12` parse. Anything that is not
 * one of those returns null and the caller falls back to a text search — a
 * search box that rejected `TDS notice` because it is not a number would be a
 * worse search box.
 */
export function parseHumanId(input: string): ParsedHumanId | null {
	const match = /^#?(v?)(\d+)$/i.exec(input.trim())
	if (!match) return null

	const [, marker, digits] = match
	if (digits === undefined) return null

	const sequence = Number(digits)
	if (!Number.isSafeInteger(sequence) || sequence <= 0) return null

	return { kind: marker ? "vetting" : "query", sequence }
}

/**
 * The path segment for a record, from its rendered reference.
 *
 * `/queries/{queryRef}` and `/vetting-requests/{vettingRef}` take the
 * human-readable number "with or without the leading `#`", so the `#` is dropped
 * and the rest passed through — `#V12` becomes `V12`, which is exactly what the
 * vetting route documents as its example.
 *
 * ENCODED, always. A reference reaches this from a route parameter, which is to
 * say from a URL somebody else may have written; a value carrying `/` or `..`
 * must address a segment of a path rather than shaping one.
 */
export function referenceParam(reference: string | number): string {
	return encodeURIComponent(String(reference).trim().replace(/^#/, ""))
}

/**
 * Fills in the numeric `sequence` a record's `reference` encodes.
 *
 * Used as a Zod `preprocess` on every schema that reads a query or a vetting
 * request. The service sends only the rendered string; screens, sorts and route
 * parameters want the number, and deriving it once here is what stops each of
 * them from slicing a `#` off by hand.
 *
 * A record that already carries a numeric `sequence` is returned untouched, so a
 * fixture written in the package's own shape still parses.
 */
export function withReference(value: unknown): unknown {
	if (value === null || typeof value !== "object" || Array.isArray(value)) return value

	const record = value as Record<string, unknown>
	if (typeof record.sequence === "number") return record
	if (typeof record.reference !== "string") return record
	const parsed = parseHumanId(record.reference)
	return parsed === null ? record : { ...record, sequence: parsed.sequence }
}
